import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import moment from "moment-timezone";
import { useDispatch } from "react-redux";

import { getUserCredential } from "src/lib/cookies";

import {
  getContractForProvider,
  getContractTypesForProvider,
  getLocationsForProvider,
  getTimesheetListByProvider,
} from "../../services";
import { calcuateAdvancedRate } from "../../utils/calculateAdvancedRate";
import { formatAmount, formatDuration, parseTimeInput } from "../../utils/payRequestConstants";

const RATE_DEBOUNCE_MS = 1000;

/*
 * Owns the add-pay-request form: the contractual selections with their
 * cascading resets and fetches, and the per-date rate rows derived from them
 * plus the times typed on the calendar cells.
 *
 * Ported from the mobile `usePayRequestForm` hook. Rates are approximations by
 * design — the contract's rate model decides the final value server-side.
 */
export const useAddPayRequestForm = ({
  selectedDates = [],
  inputValues,
  initialTask,
  providerId,
  profileRows = [],
  selectedProfile = null,
  onPositionSelect,
  editPositionLabel = null,
}) => {
  const dispatch = useDispatch();
  const cookiesData = useMemo(() => getUserCredential(), []);

  const [locationList, setLocationList] = useState([]);
  const [contractTypesList, setContractTypesList] = useState([]);
  const [contractData, setContractData] = useState(undefined);

  const [location, setLocation] = useState();
  const [payRequestType, setPayRequestType] = useState();
  const [payRequestName, setPayRequestName] = useState();
  const [shiftType, setShiftType] = useState();
  const [description, setDescription] = useState("");
  const [selectedContractMstrKey, setSelectedContractMstrKey] = useState(null);
  const [isAmountContract, setIsAmountContract] = useState(false);
  const [thresholdUnitType, setThresholdUnitType] = useState(null);
  const [allTimesheetListData, setAllTimesheetListData] = useState([]);
  const [calculationData, setCalculationData] = useState();
  const [isCalculating, setIsCalculating] = useState(false);
  const [gracePeriodDays, setGracePeriodDays] = useState(null);

  const debounceRef = useRef(null);

  // Convert API contract types to picker options
  const payRequestTypeList = useMemo(() => {
    if (!contractTypesList) return [];
    return contractTypesList.map((item) => ({
      id: Number(item.contract_type_mstr_key),
      title: String(item.contract_type),
    }));
  }, [contractTypesList]);

  // Extract contract names (payment request names) from the selected type
  const payRequestNameList = useMemo(() => {
    if (!payRequestType) return [];

    const selectedTypeData = contractTypesList.find((type) => type.contract_type_mstr_key === payRequestType.id);
    if (!selectedTypeData?.contracts_details) return [];

    return selectedTypeData.contracts_details.map((detail) => ({
      id: detail.contract_name_mstr_key,
      title: detail.contract_name,
      contract_mstr_key: detail.default_contract_mstr_key,
      show_shift_type_picker: detail.show_shift_type_picker,
    }));
  }, [payRequestType, contractTypesList]);

  // Extract shift types from the selected name
  const availableShiftTypes = useMemo(() => {
    if (!payRequestType || !payRequestName) return [];

    const selectedTypeData = contractTypesList.find((type) => type.contract_type_mstr_key === payRequestType.id);
    const selectedContract = selectedTypeData?.contracts_details?.find(
      (detail) => detail.contract_name_mstr_key === payRequestName.id
    );

    return (
      selectedContract?.shift_types?.map((st) => ({
        id: st.shift_type_mstr_key,
        title: st.shift_type_name,
        contract_mstr_key: st.contract_mstr_key,
      })) ?? []
    );
  }, [payRequestType, payRequestName, contractTypesList]);

  // Each selection resets everything downstream of it.
  const handleLocationSelect = useCallback(
    (item) => {
      if (location?.id === item.id) return;
      setLocation(item);
      setPayRequestType(undefined);
      setPayRequestName(undefined);
      setShiftType(undefined);
      setSelectedContractMstrKey(null);
    },
    [location]
  );

  const handleTypeSelect = useCallback(
    (item) => {
      if (payRequestType?.id === item.id) return;
      setPayRequestType(item);
      setPayRequestName(undefined);
      setShiftType(undefined);
      setSelectedContractMstrKey(null);
    },
    [payRequestType]
  );

  const handleNameSelect = useCallback((item) => {
    setPayRequestName(item);
  }, []);

  const handleShiftTypeSelect = useCallback((item) => {
    setAllTimesheetListData([]);
    setShiftType(item);
    setSelectedContractMstrKey(item.contract_mstr_key ?? null);
  }, []);

  // A different provider cannot keep the previous provider's location, contract, or timesheets.
  const providerIdRef = useRef(undefined);
  useEffect(() => {
    const prevProviderId = providerIdRef.current;
    providerIdRef.current = providerId;
    if (prevProviderId === undefined || prevProviderId === providerId) return;

    setLocation(undefined);
    setPayRequestType(undefined);
    setPayRequestName(undefined);
    setShiftType(undefined);
    setSelectedContractMstrKey(null);
    setContractData(undefined);
    setAllTimesheetListData([]);
  }, [providerId]);

  // Fetch locations once the provider is known
  useEffect(() => {
    if (!providerId) return;

    let cancelled = false;
    getLocationsForProvider(cookiesData, dispatch, providerId).then((rows) => {
      if (cancelled) return;
      setLocationList(
        rows.map((it) => ({
          id: it.location_mstr_key,
          title: it.location_name,
          location_mstr_key: it.location_mstr_key,
        }))
      );
    });
    return () => {
      cancelled = true;
    };
  }, [providerId, cookiesData, dispatch]);

  // Fetch contract types when a location is selected
  useEffect(() => {
    if (!providerId || !location?.id) return;

    let cancelled = false;
    getContractTypesForProvider(cookiesData, dispatch, providerId, location.id).then((rows) => {
      if (!cancelled) setContractTypesList(rows);
    });
    return () => {
      cancelled = true;
    };
  }, [location?.id, providerId, cookiesData, dispatch]);

  // Initialise / auto-select each level
  useEffect(() => {
    if (initialTask?.location_mstr_key) {
      const taskLocation = locationList.find((loc) => loc.id === initialTask.location_mstr_key);
      if (taskLocation) setLocation(taskLocation);
    } else if (locationList.length === 1) {
      setLocation(locationList[0]);
    }
  }, [initialTask, locationList]);

  useEffect(() => {
    if (!payRequestTypeList.length) return;

    if (initialTask?.contract_type_mstr_key) {
      const taskType = payRequestTypeList.find((type) => type.id === initialTask.contract_type_mstr_key);
      if (taskType) setPayRequestType(taskType);
    } else if (payRequestTypeList.length === 1) {
      setPayRequestType(payRequestTypeList[0]);
    } else {
      setPayRequestType(undefined);
    }
  }, [initialTask, payRequestTypeList]);

  useEffect(() => {
    if (!payRequestNameList.length) return;

    if (initialTask?.contract_name_mstr_key) {
      const taskName = payRequestNameList.find((name) => name.id === initialTask.contract_name_mstr_key);
      if (taskName) {
        setPayRequestName(taskName);
        setSelectedContractMstrKey(taskName.contract_mstr_key ?? null);
      }
    } else if (payRequestNameList.length === 1) {
      const [firstItem] = payRequestNameList;
      setPayRequestName(firstItem);
      setSelectedContractMstrKey(firstItem.contract_mstr_key ?? null);
    } else {
      setPayRequestName(undefined);
      setSelectedContractMstrKey(null);
    }
  }, [initialTask, payRequestNameList]);

  useEffect(() => {
    if (initialTask?.shift_type_mstr_key && availableShiftTypes.length > 0) {
      const taskShift = availableShiftTypes.find((shift) => shift.id === initialTask.shift_type_mstr_key);
      if (taskShift) {
        setShiftType(taskShift);
        setSelectedContractMstrKey(taskShift.contract_mstr_key ?? null);
      }
    } else if (availableShiftTypes.length > 0) {
      const [firstItem] = availableShiftTypes;
      setShiftType(firstItem);
      setSelectedContractMstrKey(firstItem.contract_mstr_key ?? null);
    }
  }, [initialTask, availableShiftTypes]);

  // A new contract invalidates the cached contract detail.
  useEffect(() => {
    if (selectedContractMstrKey) setContractData(undefined);
  }, [selectedContractMstrKey]);

  const fields = useMemo(() => {
    const arr = [
      {
        key: "location",
        label: "Location",
        options: locationList ?? [],
        selection: location,
        onSelect: handleLocationSelect,
      },
      {
        key: "payRequestType",
        label: "Pay Request Type",
        options: payRequestTypeList ?? [],
        selection: payRequestType,
        onSelect: handleTypeSelect,
        disabled: !location,
      },
      {
        key: "payRequestName",
        label: "Pay Request Name",
        options: payRequestNameList ?? [],
        selection: payRequestName,
        onSelect: handleNameSelect,
        disabled: !payRequestType,
      },
    ];

    if (payRequestName?.show_shift_type_picker) {
      arr.push({
        key: "shiftType",
        label: "Shift Type",
        options: availableShiftTypes,
        selection: shiftType,
        onSelect: handleShiftTypeSelect,
        disabled: !payRequestName,
      });
    }

    const showPosition =
      !initialTask && profileRows.length > 1 && profileRows.every((row) => row.provider_id && row.position_name);
    if (showPosition) {
      const options = profileRows.map((row) => ({
        id: row.org_position_key,
        title: `${row.provider_id} / ${row.position_name}`,
      }));
      arr.unshift({
        key: "position",
        label: "Position",
        options,
        selection: options.find((option) => option.id === selectedProfile?.org_position_key),
        onSelect: (item) => {
          if (item?.id === selectedProfile?.org_position_key) return;
          const row = profileRows.find((profile) => profile.org_position_key === item.id);
          if (row) onPositionSelect?.(row);
        },
      });
    }

    // An edit stays on its own position, so it is shown and never chosen
    if (initialTask && editPositionLabel) {
      const editPosition = { id: initialTask.org_position_key, title: editPositionLabel };
      arr.unshift({
        key: "position",
        label: "Position",
        options: [editPosition],
        selection: editPosition,
        onSelect: () => {},
        disabled: true,
      });
    }

    return arr;
  }, [
    location,
    locationList,
    payRequestType,
    payRequestTypeList,
    payRequestName,
    payRequestNameList,
    shiftType,
    availableShiftTypes,
    handleLocationSelect,
    handleTypeSelect,
    handleNameSelect,
    handleShiftTypeSelect,
    initialTask,
    profileRows,
    selectedProfile,
    onPositionSelect,
    editPositionLabel,
  ]);

  const isShiftContract = String(thresholdUnitType ?? "").toLowerCase() === "shift";

  // No unit yet (no contract selected) defaults to hours, not quantity.
  const normalizedUnitType = String(thresholdUnitType ?? "").toLowerCase();
  const isNotHoursMinutesContract =
    normalizedUnitType !== "" &&
    normalizedUnitType !== "hours" &&
    normalizedUnitType !== "minutes" &&
    normalizedUnitType !== "days";

  // One raw row per selected date. Sorted by date so the engine's cumulative
  // tier walk is deterministic.
  const rows = useMemo(() => {
    return [...selectedDates].sort().map((date) => {
      const raw = inputValues?.get(date) ?? "";

      // A shift-threshold contract is entered as a decimal quantity, not an
      // HH:MM time — hours/minutes stay zero rather than being parsed out of it,
      // and the raw quantity is carried separately so the rate table can show it.
      if (isNotHoursMinutesContract) {
        return {
          date,
          label: moment(date, "YYYY-MM-DD").format("MMM D"),
          hours: "0",
          minutes: "0",
          shifts: raw,
          rate: 0,
          amount: 0,
          shift: shiftType,
        };
      }

      const time = raw ? parseTimeInput(raw) : null;

      return {
        date,
        label: moment(date, "YYYY-MM-DD").format("MMM D"),
        hours: time ? String(time.hours) : "",
        minutes: time ? String(time.minutes).padStart(2, "0") : "",
        rate: 0,
        amount: 0,
        shift: shiftType,
      };
    });
  }, [selectedDates, inputValues, shiftType, isNotHoursMinutesContract]);

  /*
   * Contract detail + the provider's existing timesheets for that period.
   * Fires as soon as the contract selection resolves — same trigger as the
   * dialog's own /get-available-pay-request-dates call — rather than waiting
   * for a date to be clicked on the calendar. Without a date yet, the service
   * date defaults to today (getTimesheetListByProvider's own fallback).
   */
  useEffect(() => {
    if (!providerId || !location || !payRequestType || !selectedContractMstrKey || contractData !== undefined) {
      return;
    }

    getContractForProvider(cookiesData, dispatch, {
      providerId,
      locationMstrKey: location.id,
      contractMstrKey: selectedContractMstrKey,
    })
      .then(async (rowResult) => {
        setContractData(rowResult);

        const contract = rowResult?.[0];
        if (!contract) return;

        setIsAmountContract(contract?.thresold_unit_type === "amount");
        setThresholdUnitType(contract?.thresold_unit_type ?? null);
        setGracePeriodDays(contract?.grace_period_days ?? null);

        const sortedDates = [...selectedDates].sort();
        const timesheets = await getTimesheetListByProvider(cookiesData, dispatch, {
          providerId,
          taskCategoryMstrKey: payRequestType?.id?.toString(),
          periodType: contract?.period_type || "monthly",
          contractStartDate: contract?.contract_start_date || null,
          serviceDate: sortedDates.length > 0 ? moment(sortedDates[0], "YYYY-MM-DD").toDate() : null,
        });
        setAllTimesheetListData(timesheets);
      })
      .catch((err) => console.error(err));
  }, [
    providerId,
    location,
    payRequestType,
    selectedContractMstrKey,
    selectedDates,
    contractData,
    cookiesData,
    dispatch,
  ]);

  // Debounced rate calculation. Amount contracts are priced server-side only.
  // Shift-threshold contracts are priced client-side too — the engine reads
  // each row's `shifts` quantity instead of hours/minutes for those.
  useEffect(() => {
    if (isAmountContract) {
      setCalculationData({ rowsData: [], multiTierStr: "" });
      setIsCalculating(false);
      return;
    }

    if (rows.length === 0) {
      setCalculationData({ rowsData: [], multiTierStr: "" });
      setIsCalculating(false);
      return;
    }

    if (!contractData || !selectedContractMstrKey) return;

    /*
     * Only dates with a time entered get priced.
     *
     * Mobile instead aborted the whole recalculation whenever any row was
     * blank, which froze every other row's price too — and misfired on valid
     * input, since a 0:30 row and a :00 row together satisfied both halves of
     * its check. Filtering is also the correct input for the engine: a
     * days-threshold contract tiers by distinct work-day index, so a
     * zero-minute row would push every later row up a tier.
     */
    const pricedRows = rows.filter((row) => (inputValues?.get(row.date) ?? "").trim() !== "");

    if (pricedRows.length === 0) {
      setCalculationData({ rowsData: [], multiTierStr: "" });
      setIsCalculating(false);
      return;
    }

    setIsCalculating(true);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      /*
       * calculateAdvancedRate is a line-for-line transliteration of the mobile
       * engine, so every defence against its rough edges lives here instead of
       * in it. Do not push any of this down into the engine — that file has to
       * stay diffable against the mobile source.
       *
       * - Rows must be fresh objects on every run. The engine keeps a shallow
       *   `[...rows]` and its step-hours branch does `row.amount +=`, while
       *   this effect re-fires on contractData / payRequestName / shiftType /
       *   allTimesheetListData with `rows` still the same memoised array. That
       *   happens on every open, since allTimesheetListData is set right after
       *   contractData, so without the clone amounts would compound.
       * - contractData is copied because two tier branches sort it in place,
       *   which would reorder the state array and the tier tooltip with it.
       * - payRequestName / shiftType can be undefined (shiftType stays unset
       *   whenever the contract has no shift-type picker) and the engine reads
       *   `.id` off them unguarded. Same for timesheet rows missing `status`,
       *   which arrive straight from row_result with no shape guarantee.
       */
      try {
        const advancedCalculation = await calcuateAdvancedRate(
          [...(contractData || [])],
          pricedRows.map((row) => ({ ...row, rate: 0, amount: 0 })),
          selectedContractMstrKey,
          payRequestName ?? {},
          shiftType ?? {},
          allTimesheetListData ?? []
        );
        setCalculationData(advancedCalculation);
      } catch (error) {
        /*
         * The engine throws in its days-threshold tier branch whenever the
         * tier has a non-null cap — it calls .toFixed() on the string that
         * calculateTierBalance returns. Clear the rates rather than leave the
         * form stuck with isCalculating true and no way out.
         */
        console.error("Rate calculation failed:", error);
        setCalculationData({ rowsData: [], multiTierStr: "" });
      } finally {
        setIsCalculating(false);
      }
    }, RATE_DEBOUNCE_MS);

    return () => clearTimeout(debounceRef.current);
  }, [
    rows,
    inputValues,
    contractData,
    selectedContractMstrKey,
    isAmountContract,
    isNotHoursMinutesContract,
    payRequestName,
    shiftType,
    allTimesheetListData,
  ]);

  /*
   * What the Transactional Details table renders.
   *
   * Membership comes from the calendar selection so a date shows up the instant
   * it is clicked; pricing is an overlay that is allowed to lag the 1s debounce.
   * Deriving membership from the priced rows instead meant a selected-but-
   * unpriced date was invisible.
   */
  const displayRows = useMemo(() => {
    const pricedByDate = new Map((calculationData?.rowsData ?? []).map((row) => [row.date, row]));

    return rows.map((row) => {
      const priced = pricedByDate.get(row.date);
      return priced ? { ...row, ...priced } : row;
    });
  }, [rows, calculationData]);

  const estimatedTotal = useMemo(() => {
    let totalAmount = 0;

    // Shift-threshold contracts have no hours/minutes to sum — show the total
    // quantity of shifts entered on the calendar instead of a duration.
    if (isNotHoursMinutesContract) {
      const totalShifts = displayRows.reduce((sum, row) => sum + (Number(row.shifts) || 0), 0);
      displayRows.forEach((row) => {
        totalAmount += Number(row.amount || 0);
      });

      const unitLabel = thresholdUnitType ? thresholdUnitType.charAt(0).toUpperCase() + thresholdUnitType.slice(1) : "";

      return {
        amount: `$ ${formatAmount(totalAmount)}`,
        duration: `${totalShifts.toFixed(2)} ${unitLabel}`.trim() + (unitLabel ? "(s)" : ""),
      };
    }

    let totalMinutes = 0;

    displayRows.forEach((row) => {
      totalMinutes += Number(row.hours || 0) * 60 + Number(row.minutes || 0);
      totalAmount += Number(row.amount || 0);
    });

    return {
      amount: `$ ${formatAmount(totalAmount)}`,
      duration: formatDuration(Math.floor(totalMinutes / 60), totalMinutes % 60),
    };
  }, [displayRows, isNotHoursMinutesContract, thresholdUnitType]);

  return {
    fields,
    rows,
    displayRows,
    calculationData,
    estimatedTotal,
    isCalculating,
    description,
    setDescription,
    location,
    payRequestType,
    payRequestName,
    shiftType,
    selectedContractMstrKey,
    isAmountContract,
    thresholdUnitType,
    isShiftContract,
    isNotHoursMinutesContract,
    contractData,
    allTimesheetListData,
    gracePeriodDays,
  };
};

export default useAddPayRequestForm;
